import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Button,
  HairlineCard,
  Icons,
  iconSize,
  Stack,
  StatusChip,
  Typography,
} from '@munifocus/ui';
import { useCostRecovery } from '../../lib/cost-recovery-api';
import { day, money, RECOVERY_STATUS_KIND } from '../../lib/cost-recovery-display';

/**
 * Where a file's cost recovery stands, in one card, with the way in.
 *
 * Renders nothing at all while the account cannot be read — cost recovery off
 * at this municipality (404), or not this person's business (403) — so a town
 * that has not turned it on sees exactly the file page it always saw.
 */
export function CostRecoverySummaryCard({
  fileId,
  enabled = true,
}: {
  fileId: string;
  /**
   * The file detail's `modules.costRecovery`. False skips the request: with
   * the module off it could only answer 404, and did, on every file in the town.
   */
  enabled?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { data } = useCostRecovery(enabled ? fileId : undefined);
  if (!data) return null;

  const lang = i18n.language;
  const b = data.balance;
  const nothingYet = data.items.length === 0;

  return (
    <HairlineCard sx={{ p: 2, mt: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}
      >
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Icons.Banknote size={iconSize.md} aria-hidden />
            <Typography variant="subtitle2" component="h2">
              {t('gov.costRecovery.title')}
            </Typography>
            <StatusChip
              status={RECOVERY_STATUS_KIND[data.status]}
              label={t(`gov.costRecovery.status.${data.status}`)}
            />
          </Stack>
          {nothingYet ? (
            <Typography variant="body2" color="text.secondary">
              {t('gov.costRecovery.summary.empty')}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {data.status === 'open'
                ? t('gov.costRecovery.summary.unbilled', {
                    pending: money(data.totals.pendingCents, lang),
                    unbilled: money(data.totals.unbilledCents, lang),
                  })
                : t('gov.costRecovery.summary.balance', {
                    balance: money(b.balanceCents, lang),
                    billed: money(b.billedCents, lang),
                    paid: money(b.paidCents, lang),
                    due: day(data.dueOn, lang),
                  })}
            </Typography>
          )}
        </Stack>
        <Button
          component={RouterLink}
          to={`/files/${fileId}/cost-recovery`}
          variant="outlined"
          size="small"
          endIcon={<Icons.ChevronRight size={iconSize.sm} />}
          sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, flexShrink: 0 }}
        >
          {t('gov.costRecovery.summary.open')}
        </Button>
      </Stack>
    </HairlineCard>
  );
}
